import { Fragment, useEffect, useId, useState } from 'react';

import { Button, Icon } from '@ds/core';
import { Alert, Modal } from '@ds/feedback';
import { FormField, Input, Select } from '@ds/forms';
import { useAviso } from '@/comuns/toast';
import { CORES_DA_ETAPA, MODELOS_DE_FUNIL, etapasDoModelo } from '@/data/funis';
import type { ModeloDeFunil } from '@/data/funis';
import { dados } from '@/data/porta';
import type { Funil } from '@/data/tipos';

export interface NovoPipelineModalProps {
    open: boolean;
    onClose: () => void;
    onCriado: (funil: Funil) => void;
}

/**
 * "Novo pipeline", como no protótipo do CRM — trilha T7.
 *
 * Nome e modelo de etapas. O protótipo tinha também "responsável padrão", que
 * ficou de fora: lead novo entra no pipeline padrão da clínica, então o campo não
 * mudaria nada — e controle que não muda nada é o que este projeto não deixa.
 */
export function NovoPipelineModal({ open, onClose, onCriado }: NovoPipelineModalProps) {
    const aviso = useAviso();
    const idNome = useId();
    const idModelo = useId();
    const [nome, setNome] = useState('');
    const [modelo, setModelo] = useState<ModeloDeFunil>('comercial');
    const [erro, setErro] = useState<string | null>(null);
    const [criando, setCriando] = useState(false);

    useEffect(() => {
        if (!open) return;
        setNome('');
        setModelo('comercial');
        setErro(null);
    }, [open]);

    if (!open) return null;

    const etapas = etapasDoModelo(modelo);

    const criar = async (): Promise<boolean> => {
        if (!nome.trim()) {
            setErro('Dê um nome ao pipeline.');
            return false;
        }
        setCriando(true);
        setErro(null);
        try {
            const funil = await dados().criarFunil({ nome, modelo });
            aviso({ title: 'Pipeline criado', description: `${funil.nome} · ${funil.etapas.length} etapas, pronto para receber leads` });
            onCriado(funil);
            onClose();
            return true;
        } catch (e) {
            setErro(e instanceof Error ? e.message : 'Não foi possível criar o pipeline.');
            return false;
        } finally {
            setCriando(false);
        }
    };

    return (
        <Modal
            open onClose={onClose} width={500} title="Novo pipeline"
            description="Um pipeline por processo comercial — vendas, pós-venda, indicações."
            dirty={nome !== '' || modelo !== 'comercial'} onSave={criar}
            footer={
                <>
                    <Button variant="secondary" onClick={onClose}>Cancelar</Button>
                    <Button onClick={() => void criar()} loading={criando}>Criar pipeline</Button>
                </>
            }
        >
            <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
                {erro && <Alert tone="danger" dismissible={false}>{erro}</Alert>}
                <FormField label="Nome do pipeline" htmlFor={idNome} required>
                    <Input id={idNome} value={nome} onChange={(v) => { setNome(v); setErro(null); }} placeholder="Ex.: Indicações" />
                </FormField>
                <FormField label="Modelo de etapas" htmlFor={idModelo} hint="Você pode renomear e reordenar depois">
                    <Select
                        id={idModelo} value={modelo} onChange={(v) => setModelo(v as ModeloDeFunil)}
                        options={MODELOS_DE_FUNIL.map((m) => ({ value: m.value, label: m.label }))}
                    />
                </FormField>
                <div
                    aria-label="Etapas do modelo"
                    style={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 8, padding: '11px 13px', background: 'var(--surface-secondary)', border: '1px solid var(--border-subtle)', borderRadius: 'var(--radius-md)' }}
                >
                    {etapas.map((e, i) => (
                        <Fragment key={e.titulo}>
                            <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6, fontSize: 12.5, color: 'var(--neutral-700)' }}>
                                <span aria-hidden="true" style={{ width: 7, height: 7, borderRadius: '50%', background: CORES_DA_ETAPA[e.cor] }} />
                                {e.titulo}
                            </span>
                            {i < etapas.length - 1 && <Icon name="chevron-right" size={13} style={{ color: 'var(--neutral-400)' }} />}
                        </Fragment>
                    ))}
                </div>
            </div>
        </Modal>
    );
}
